import React, { useCallback, useState } from 'react'
import Fuse from 'fuse.js'
import { useQuery } from '@tanstack/react-query'
import { SpinnerWrapper } from '../../common/SpinnerWrapper'
import { ErrorView } from '../../../views/error/ErrorView'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { IllustrationChoices } from '../../common/Illustration'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'
import { RegisterGroupListItemBase } from './RegisterGroupListItemBase'
import { SearchBar } from '../../common/SearchBar'
import { groupQueries } from '../../../api/groups'

export const RegisterGroupList = (): React.ReactNode => {
  const [searchString, setSearchString] = useState('')
  const { translate } = useAppTranslation()
  const { society, isLoading: isLoadingSociety } = useCurrentSociety()

  const { data: groups, isLoading: isLoadingGroups } = useQuery(
    groupQueries.getGroupsPerSociety(society?._id)
  )

  const getFilteredGroups = useCallback(() => {
    const options = {
      threshold: 0.1,
      keys: ['name'],
    }
    const _groups = groups || []

    const fuse = new Fuse(_groups, options)

    const filteredEntrances =
      searchString === ''
        ? _groups
        : fuse.search(searchString).map((result) => result.item)

    return filteredEntrances
  }, [groups, searchString])

  const onSearchBarChange = (e: React.FormEvent<HTMLInputElement>): void => {
    setSearchString(e.currentTarget.value)
  }
  const loading = isLoadingSociety || isLoadingGroups

  if (loading) {
    return <SpinnerWrapper />
  }

  if (!loading && groups && groups.length === 0) {
    return (
      <ErrorView
        title={translate('common.emptyState.title')}
        subtitle={translate('common.emptyState.subtitle')}
        illustration={IllustrationChoices.EMPTY}
      />
    )
  }

  return (
    <div className="w-full space-y-10">
      <SearchBar
        className="w-full md:w-80"
        widthClassName="flex grow"
        value={searchString}
        onChange={onSearchBarChange}
      />
      {getFilteredGroups().map((group) => (
        <RegisterGroupListItemBase key={group._id} group={group} />
      ))}
    </div>
  )
}
